const REQUEST_CONTEXT_KEY = 'localeSwitcherReady'

export interface LocaleSwitcherReadyState {
  promise: Promise<void>
  resolve: () => void
  reset: () => void
}

function createState(): LocaleSwitcherReadyState {
  let resolveFn: () => void
  let promise = new Promise<void>((resolve) => {
    resolveFn = resolve
  })
  return {
    get promise() {
      return promise
    },
    get resolve() {
      return () => resolveFn?.()
    },
    reset() {
      promise = new Promise<void>((resolve) => {
        resolveFn = resolve
      })
    },
  }
}

let clientState: LocaleSwitcherReadyState | null = null

/**
 * Shared state for coordinating LocaleSwitcher with pages that have
 * locale-specific slugs (category, magazine). Pages call reset() at setup
 * start and resolve() after Strapi/data is loaded and store is set.
 * LocaleSwitcher awaits the promise on server so links use correct slugs.
 * Uses request context (server) / module ref (client) so the Promise is
 * never stored in useState and thus not serialized for the payload.
 */
export const useLocaleSwitcherReady = (): LocaleSwitcherReadyState => {
  if (import.meta.server) {
    const event = useRequestEvent()
    if (!event?.context) {
      return createState()
    }
    if (!(event.context as Record<string, unknown>)[REQUEST_CONTEXT_KEY]) {
      ;(event.context as Record<string, LocaleSwitcherReadyState>)[
        REQUEST_CONTEXT_KEY
      ] = createState()
    }
    return (event.context as Record<string, LocaleSwitcherReadyState>)[
      REQUEST_CONTEXT_KEY
    ]!
  }
  if (!clientState) {
    clientState = createState()
  }
  return clientState
}

/**
 * Returns true when route meta has localeSpecificSlug.
 * Set definePageMeta({ localeSpecificSlug: true }) on any page that has
 * locale-specific slugs and calls useLocaleSwitcherReady().reset()/resolve().
 * Exported for unit testing.
 */
export const isLocaleSpecificRouteFromMeta = (
  meta: Record<string, unknown> | undefined
): boolean => Boolean(meta?.localeSpecificSlug)

/**
 * Returns true if the current route has locale-specific slugs and
 * LocaleSwitcher should wait for page data before rendering links.
 */
export const useIsLocaleSpecificRoute = () => {
  const route = useRoute()
  return computed(() =>
    isLocaleSpecificRouteFromMeta(route.meta as Record<string, unknown>)
  )
}
